/**
 * FicheClient360 store: wires DI and composes domain slices.
 * Types live in ./types, helpers in ./helpers, slice logic in ./slices.
 */
import { create } from "zustand";
import { ServiceContainer } from "@/core/di";

import {
  initialState,
  type FicheClient360State,
  type FicheClient360StoreDeps,
} from "./types";
import { createSyntheseSlice } from "./slices/syntheseSlice";
import { createKycSlice } from "./slices/kycSlice";
import { createAccountsSlice } from "./slices/accountsSlice";
import { createDocumentsSlice } from "./slices/documentsSlice";
import { createOtpSlice } from "./slices/otpSlice";

// Re-export types for consumers that import from the store
export type {
  ContactField,
  OTPSendResult,
  OTPVerifyResult,
  KYCUpdateResult,
  FicheClient360StoreDeps,
} from "./types";

export const createFicheClient360Store = (deps: FicheClient360StoreDeps) =>
  create<FicheClient360State>()((set, get) => {
    return {
      ...initialState,

      ...createSyntheseSlice(deps, set),
      ...createKycSlice(deps, set, get),
      ...createAccountsSlice(deps, set),
      ...createDocumentsSlice(deps, set),
      ...createOtpSlice(deps, set, get),

      reset: () => {
        set(initialState);
      },
    } as FicheClient360State;
  });

const defaultServiceContainer = ServiceContainer.getInstance();

const defaultDeps: FicheClient360StoreDeps = {
  screeningReportsRepository:
    defaultServiceContainer.getRepository(
      "screeningReports",
    ),
  accountsRepository:
    defaultServiceContainer.getRepository("accounts"),
  customerKYCRepository:
    defaultServiceContainer.getRepository("customerKYC"),
  customerDocumentsRepository:
    defaultServiceContainer.getRepository(
      "customerDocuments",
    ),
  contactVerificationService:
    defaultServiceContainer.getRepository(
      "contactVerification",
    ),
};

export const useFicheClient360Store = createFicheClient360Store(defaultDeps);
